/**
 * The `/…/studies/$id` URL search. Kept out of `StudyPage` because the route's `validateSearch`
 * runs before the page's code loads: importing it from the page would put the whole study
 * workspace in the app's entry chunk.
 */

import { isMatrixViewKey, type MatrixViewKey } from "@/features/studies/matrixViews";
import { parseQuoteTurnIndex } from "@/features/studies/quoteDeepLink";
import { SCOPE_TABS } from "@/features/studies/scopeTabs";

export const STAGE_TABS = [
  "scope",
  "documents",
  "sourcing",
  "conversations",
  "matrix",
  "analysis",
  "sources",
  "hypotheses",
  "exhibits",
  "report",
  "settings",
] as const;
export type StageTab = (typeof STAGE_TABS)[number];

// The lifecycle-timeline steps double as tab keys; each view falls back to its own default for
// steps its stage hasn't unlocked (e.g. ?tab=report on a live study), so stale links degrade
// gracefully.
const TAB_KEYS = new Set<string>(STAGE_TABS);
// The scope document's own steps ride alongside `?tab=scope` rather than inside it: they select a
// view within one surface, so collapsing them into the stage vocabulary would conflate the two.
const SCOPE_STEP_KEYS = new Set<string>(SCOPE_TABS);

/** The `/…/studies/$id` search: tab, scope step and deep links. Unknown values drop. */
export function validateStudySearch(search: Record<string, unknown>): {
  tab?: string;
  step?: string;
  view?: MatrixViewKey;
  q?: string;
  h?: string;
  family?: string;
  code?: string;
  session?: string;
  turn?: number;
} {
  const legacyResponsesTab = search.tab === "responses";
  const view =
    typeof search.view === "string" && isMatrixViewKey(search.view)
      ? search.view
      : legacyResponsesTab
        ? "responses"
        : undefined;
  const turn = parseQuoteTurnIndex(search.turn);
  return {
    ...(legacyResponsesTab
      ? { tab: "matrix" }
      : typeof search.tab === "string" && TAB_KEYS.has(search.tab)
        ? { tab: search.tab }
        : {}),
    ...(typeof search.step === "string" && SCOPE_STEP_KEYS.has(search.step)
      ? { step: search.step }
      : {}),
    ...(view ? { view } : {}),
    ...(typeof search.q === "string" && search.q.length > 0 ? { q: search.q } : {}),
    ...(typeof search.h === "string" && search.h.length > 0 ? { h: search.h } : {}),
    ...(typeof search.family === "string" && search.family.length > 0
      ? { family: search.family }
      : {}),
    ...(typeof search.code === "string" && search.code.length > 0 ? { code: search.code } : {}),
    ...(typeof search.session === "string" && search.session.length > 0
      ? { session: search.session }
      : {}),
    ...(turn != null ? { turn } : {}),
  };
}
